import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Signal,
  WritableSignal,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { filter, take } from 'rxjs';
import { features } from '@consts/text.const';
import { routerSegments } from '@consts/routes';
import { CaseCategoriesModel } from '@models/case-categories.model';
import { IntelligenceModels } from '@models/intelligence.model';
import { CaseCategoriesActions } from '@states/case-category/case-category.actions';
import { ConfirmDialogModel, ConfirmDialogService, UiButtonComponent, UiNoDataComponent } from '@ui';
import { DatasetTableConfig, UiDatasetTableComponent } from '@ui/lib/ui-kit/ui-dataset-table';
import {
  CategoryDialogComponent,
  CreateCategoryDialogData,
} from '../../../../../../pages/database/pages/categories/components/category-dialog/category-dialog.component';
import { IntelligenceResultsService } from '../../../../../../services/intelligence-results.service';
import { UiSearchComponent } from '../../../../../ui-kit/ui-search/ui-search.component';
import { createCaseManagerCategoryResultTableConfig } from './case-manager-category-result-table.util';

type Category = CaseCategoriesModel.CategoryWithStringRetention;

/** The Database → Categories page's own dialog sizing, reused for create and edit. */
const CATEGORY_DIALOG_PANEL_CLASS = 'modal-no-padding';
const CATEGORY_DIALOG_WIDTH = '548px';

/**
 * Cmd+K table of every organization case category, modelled on the
 * Database → Categories page. Filtering is client-side: `list-case-categories`
 * returns the whole organization list in one response, so there is nothing to
 * re-query.
 *
 * Creation, editing and deletion all reuse the page's own dialog and store
 * actions; the resulting category reaches this snapshot through
 * `IntelligenceResultsService`, which listens for the store success actions rather
 * than re-running `list-case-categories`.
 */
@Component({
  selector: 'app-case-manager-category-result',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslatePipe,
    UiButtonComponent,
    UiDatasetTableComponent,
    UiNoDataComponent,
    UiSearchComponent,
  ],
  templateUrl: './case-manager-category-result.component.html',
  styleUrls: ['./case-manager-category-result.component.scss'],
})
export class CaseManagerCategoryResultComponent {
  private readonly resultsService = inject(IntelligenceResultsService);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly matDialog = inject(MatDialog);
  private readonly store$ = inject(Store);
  private readonly router = inject(Router);

  protected readonly noData: typeof features.caseManagement.categories.noData =
    features.caseManagement.categories.noData;
  protected readonly noResults: typeof features.caseManagement.categories.noResults =
    features.caseManagement.categories.noResults;
  protected readonly searchQuery: WritableSignal<string> = signal<string>('');

  protected readonly tableConfig: DatasetTableConfig<Category> =
    createCaseManagerCategoryResultTableConfig();

  protected readonly response: Signal<IntelligenceModels.CaseManager.CategoriesResponse | null> =
    computed(() => {
      const result = this.resultsService.current();
      return result?.type === 'case-manager-category'
        ? (result.data as IntelligenceModels.CaseManager.CategoriesResponse)
        : null;
    });

  /**
   * Retention periods are stored as day counts and rendered as labels, exactly as
   * the page does it — the map is the only place those six values are named.
   */
  protected readonly categories: Signal<Category[]> = computed(() =>
    (this.response()?.categories ?? []).map((category) => ({
      ...category,
      retentionPeriod: this.translateService.instant(
        CaseCategoriesModel.RetentionPeriodsMap[category.retentionPeriod],
      ),
    })),
  );

  protected readonly filteredCategories: Signal<Category[]> = computed(() => {
    const query = this.searchQuery().trim().toLowerCase();
    const categories = this.categories();
    // Case-insensitive, unlike the page's bare `includes` — see case-category-selector.
    return query
      ? categories.filter((category) => (category.name ?? '').toLowerCase().includes(query))
      : categories;
  });

  public constructor() {
    this.resultsService.setHeaderActions([
      {
        icon: 'ui-icon-new-tab',
        tooltip: 'CASE_CATEGORIES.MANAGE',
        onClick: () => this.openCategoriesPage(),
      },
    ]);
    this.destroyRef.onDestroy(() => this.resultsService.setHeaderActions([]));
  }

  protected onSearchQuery(query: string): void {
    this.searchQuery.set(query);
  }

  /**
   * Open the Database → Categories dialog as-is. Opened directly rather than
   * through `RouteModalService`, matching the tags panel: a URL param would
   * survive a refresh that the Cmd+K session itself does not.
   *
   * `data` is an EMPTY OBJECT, not omitted: `MatDialogConfig.data` defaults to
   * `null` and the dialog's seeding effect reads `this.data.newName` unguarded.
   */
  protected create(): void {
    const data: CreateCategoryDialogData = {};

    this.matDialog.open(CategoryDialogComponent, {
      panelClass: CATEGORY_DIALOG_PANEL_CLASS,
      width: CATEGORY_DIALOG_WIDTH,
      data,
    });
  }

  protected onRowAction(event: IntelligenceModels.CaseManager.CategoryRowActionEvent): void {
    switch (event.actionId) {
      case IntelligenceModels.CaseManager.CategoryRowActionId.Edit:
        this.openEditDialog(event.row);
        break;
      case IntelligenceModels.CaseManager.CategoryRowActionId.Delete:
        this.deleteCategory(event.row);
        break;
    }
  }

  /**
   * Reopen the same dialog in edit mode. No hydration is needed here: the dialog
   * reads the category out of `caseCategoriesFeature` and dispatches
   * `getCategoriesList()` itself when that slice is still empty, so it seeds its
   * own form whether or not the Database page has ever been visited.
   */
  private openEditDialog(category: Category): void {
    const data: CreateCategoryDialogData = { id: category._id };

    this.matDialog.open(CategoryDialogComponent, {
      panelClass: CATEGORY_DIALOG_PANEL_CLASS,
      width: CATEGORY_DIALOG_WIDTH,
      data,
    });
  }

  /**
   * Confirm through `ConfirmDialogService` rather than the global
   * `showConfirmModal` stream: the Database page filters that shared stream on
   * `removeCaseCategory`, so a page mounted behind the Cmd+K modal would dispatch
   * a SECOND delete for one confirmation.
   *
   * The delete itself goes through the store, so the page's own list stays in step
   * and the effect owns the error toast — including the 409 raised for a category
   * that existing cases still use; this panel's snapshot is spliced by
   * `IntelligenceResultsService`, which listens for the success action.
   */
  private deleteCategory(category: Category): void {
    this.confirmDialogService
      .open({
        type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
        title: this.translateService.instant('CASE_CATEGORIES.DELETE.TITLE'),
        msg: this.translateService.instant('CASE_CATEGORIES.DELETE.MESSAGE'),
        confirm: this.translateService.instant('COMMON.DELETE'),
        cancel: this.translateService.instant('COMMON.CANCEL'),
        disableClose: true,
      })
      .pipe(
        take(1),
        filter(
          (result) =>
            result?.selection === ConfirmDialogModel.ConfirmResult.OK &&
            result.type === ConfirmDialogModel.ConfirmDialogType.CONFIRM,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.store$.dispatch(CaseCategoriesActions.deleteCategory({ id: category._id })));
  }

  /** Open the page in a NEW TAB so the current Cmd+K session stays open. */
  private openCategoriesPage(): void {
    const tree = this.router.createUrlTree(['/', routerSegments.database, routerSegments.categories]);
    const path = this.router.serializeUrl(tree);
    const url = window.location?.origin ? `${window.location.origin}${path}` : path;
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}
